import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Search, X, Box, Tag, User, Loader2, Clock } from "lucide-react";

const RECENT_KEY = "jx_recent_searches";
const readRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").slice(0, 6); }
  catch { return []; }
};
const pushRecent = (term) => {
  try {
    const next = [term, ...readRecent().filter((t) => t !== term)].slice(0, 6);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch { /* private mode */ }
};

/** Full-screen mobile search sheet. Phones had NO search at all — the
 *  navbar field is `hidden md:flex` — which made the catalogue
 *  unbrowsable for the majority of visitors. */
export default function MobileSearchSheet({ open, onClose }) {
  const nav = useNavigate();
  const inputRef = useRef(null);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState(readRecent);

  useEffect(() => {
    if (!open) return;
    setRecent(readRecent());
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    document.body.style.overflow = "hidden";
    return () => { clearTimeout(t); document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRes(null); return; }
    let dead = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search/suggest", { params: { q: term } });
        if (!dead) setRes(data);
      } catch {
        if (!dead) setRes(null);
      } finally {
        if (!dead) setLoading(false);
      }
    }, 200);
    return () => { dead = true; clearTimeout(t); };
  }, [q]);

  if (!open) return null;

  const go = (path, term) => {
    if (term) pushRecent(term);
    setQ("");
    setRes(null);
    onClose();
    nav(path);
  };
  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (term) go(`/marketplace?q=${encodeURIComponent(term)}`, term);
  };

  const hasResults = res && (res.listings?.length || res.categories?.length || res.designers?.length);

  // Rendered through a portal on <body>. The navbar uses
  // `backdrop-filter: blur()` (.glass-nav) which per spec makes the
  // header a containing block for fixed descendants — mounting the sheet
  // inline clipped it to the 64px header and killed every tap inside it.
  return createPortal((
    <div className="fixed inset-0 z-[9999] bg-background flex flex-col" data-testid="mobile-search-sheet">
      <form onSubmit={submit} className="flex items-center gap-2 px-3 py-3 border-b border-border">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search designs, categories, designers…"
            autoComplete="off"
            enterKeyHint="search"
            data-testid="mobile-search-input"
            className="w-full bg-secondary/60 border border-border rounded-full pl-9 pr-9 py-2.5 text-base focus:outline-none focus:border-primary"
          />
          {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        <button type="button" onClick={onClose} data-testid="mobile-search-close" className="p-2 rounded-full hover:bg-secondary" aria-label="Close search">
          <X className="h-5 w-5" />
        </button>
      </form>

      <div className="flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
        {q.trim().length < 2 ? (
          <div className="p-4 space-y-5">
            {recent.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Recent</p>
                {recent.map((t) => (
                  <button
                    key={t}
                    onClick={() => go(`/marketplace?q=${encodeURIComponent(t)}`, t)}
                    data-testid={`mobile-search-recent-${t}`}
                    className="w-full flex items-center gap-2.5 py-3 border-b border-border text-left"
                  >
                    <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate text-sm">{t}</span>
                  </button>
                ))}
              </div>
            )}
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Popular</p>
              <div className="flex flex-wrap gap-2">
                {["Mangalsutra", "Engagement ring", "Bangles", "Pendant", "Nose pin", "Murti"].map((t) => (
                  <button
                    key={t}
                    onClick={() => go(`/marketplace?q=${encodeURIComponent(t)}`, t)}
                    data-testid={`mobile-search-chip-${t}`}
                    className="border border-border rounded-full px-3.5 py-2 text-sm hover:border-primary hover:text-primary"
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {!hasResults && !loading && (
              <p className="px-4 py-6 text-sm text-muted-foreground">No matches for "{q.trim()}"</p>
            )}
            {res?.listings?.length > 0 && (
              <Group title="Designs">
                {res.listings.map((l) => (
                  <Row key={l.id} testid={`mobile-suggest-listing-${l.id}`} icon={<Box className="h-4 w-4 text-primary" />}
                    label={l.title} tag={l.file_type} onClick={() => go(`/design/${l.id}`, q.trim())} />
                ))}
              </Group>
            )}
            {res?.categories?.length > 0 && (
              <Group title="Categories">
                {res.categories.map((c) => (
                  <Row key={c.id} testid={`mobile-suggest-cat-${c.id}`} icon={<Tag className="h-4 w-4 text-muted-foreground" />}
                    label={c.name} onClick={() => go(`/marketplace?category=${c.id}`, q.trim())} />
                ))}
              </Group>
            )}
            {res?.designers?.length > 0 && (
              <Group title="Designers">
                {res.designers.map((d) => (
                  <Row key={d.id} testid={`mobile-suggest-designer-${d.id}`} icon={<User className="h-4 w-4 text-muted-foreground" />}
                    label={d.name} onClick={() => go(`/designer/${d.id}`, q.trim())} />
                ))}
              </Group>
            )}
            {hasResults && (
              <button
                onClick={submit}
                data-testid="mobile-search-see-all"
                className="w-full text-left px-4 py-4 text-sm font-semibold text-primary border-t border-border"
              >
                See all results for "{q.trim()}" →
              </button>
            )}
          </>
        )}
      </div>
    </div>
  ), document.body);
}

const Group = ({ title, children }) => (
  <div className="pt-2">
    <p className="px-4 pb-1 text-[10px] uppercase tracking-widest text-muted-foreground">{title}</p>
    {children}
  </div>
);

const Row = ({ icon, label, tag, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-border text-left active:bg-secondary">
    {icon}
    <span className="flex-1 truncate text-sm">{label}</span>
    {tag && <span className="font-mono text-[10px] uppercase text-muted-foreground">{tag}</span>}
  </button>
);
